import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { RevealLines, Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1708534246055-d7b149acb731?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjB0cmFkaXRpb25hbCUyMGt1cnRpJTIwbW9kZWx8ZW58MHx8fHwxNzg1ODI3NjQ3fDA&ixlib=rb-4.1.0&q=85";
const TEXTURE =
  "https://images.unsplash.com/photo-1528458909336-e7a0adfed0a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmYWJyaWMlMjB0ZXh0dXJlJTIwbmV1dHJhbHxlbnwwfHx8fDE3ODU4Mjc2NDh8MA&ixlib=rb-4.1.0&q=85";

const chapters = [
  { n: "01", t: "The Fabric", d: "We source pure cottons, silks and georgettes — chosen by hand, felt before they are ever cut." },
  { n: "02", t: "The Craft", d: "Every kurti and suit carries the mark of an artisan. Slow stitching, honest finishing, no shortcuts." },
  { n: "03", t: "The Wearer", d: "Made for the woman who dresses for herself. Rooted in tradition, cut for the way you live now." },
];

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 4)));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] grid md:grid-cols-12 items-center overflow-hidden">
        <div className="md:col-span-7 px-6 md:px-10 pt-16 md:pt-0 z-10">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="font-mono text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#8C3A3A] mb-8"
          >
            Traditional Indian Wear · Est. 2025
          </motion.p>
          <h1 className="font-serif font-light text-[15vw] md:text-[8.5vw] leading-[0.92] tracking-tight text-[#1A1A1A]">
            <RevealLines lines={["Draped in", "heritage,", "worn with"]} delay={0.35} />
            <RevealLines lines={["grace."]} delay={0.75} className="text-[#8C3A3A] italic" />
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-btn"
              className="group inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F9F8F6] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#8C3A3A] transition-colors duration-300"
            >
              Explore the Collection
              <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <p className="font-sans text-sm text-[#595959] max-w-xs leading-relaxed">Suits & kurtis crafted for the modern Indian wardrobe.</p>
          </motion.div>
        </div>

        <div className="md:col-span-5 relative h-[55vh] md:h-[92vh] mt-10 md:mt-0">
          <motion.div style={{ y, scale }} className="absolute inset-0 overflow-hidden">
            <motion.img
              src={HERO_IMG}
              alt="Woman in a maroon traditional kurti"
              initial={{ scale: 1.2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#E5E1D8] py-8 bg-[#F2EFE9]">
        <Marquee speed={40} gradient={false}>
          {["Kurtis", "Anarkali Suits", "Pure Cotton", "Handcrafted", "Free Shipping in India", "15-Day Refunds"].map((t, i) => (
            <span key={i} className="font-serif italic text-3xl md:text-5xl text-[#595959] mx-10">{t} <span className="text-[#8C3A3A] not-italic">·</span></span>
          ))}
        </Marquee>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="flex items-end justify-between mb-14">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C3A3A] mb-4">Featured</p>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tight text-[#1A1A1A]">This Season's Edit</h2>
          </Reveal>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:text-[#8C3A3A] transition-colors">
            View All <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="relative grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={TEXTURE} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-[#F2EFE9]/70" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <h2 className="font-serif text-5xl md:text-8xl tracking-tight text-[#1A1A1A] max-w-4xl leading-[0.95]">
              A quiet devotion to how things are made.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-12 mt-20">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <p className="font-mono text-sm text-[#8C3A3A] mb-4">{c.n}</p>
                <h3 className="font-serif text-3xl text-[#1A1A1A] mb-3">{c.t}</h3>
                <p className="font-sans text-sm text-[#595959] leading-relaxed">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32">
        <Reveal>
          <div className="border border-[#E5E1D8] px-8 md:px-16 py-16 md:py-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-[#1A1A1A] max-w-2xl leading-tight">
              Find the piece that feels like <span className="italic text-[#8C3A3A]">you</span>.
            </h2>
            <Link to="/shop" data-testid="cta-shop-btn" className="shrink-0 inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F9F8F6] px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#8C3A3A] transition-colors duration-300">
              Shop Now <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
